import {
  ActionIcon,
  Box,
  Divider,
  Flex,
  Grid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { LiveBlogDetailsProps } from './types';
import { FC, useState } from 'react';
import { NEWS_HEADER_IMAGE } from 'src/mockdata';
import { TitleDescriptionLiveBlog } from '@molecules/backoffice/TitleDescriptionLiveBlog';
import { TablePagination } from '@molecules/backoffice/TablePagination';
import { LivePost } from '@organisms/LivePost';
import { useTranslation } from 'next-i18next';
import AuthorsCard from '@molecules/frontoffice/AuthorsCard';
import { getFormattedDate } from '@utils/dates';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';
import { ShareMenu } from '@molecules/ShareMenu';
import { useDisclosure } from '@mantine/hooks';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import Image from 'next/image';
import { ShareIcon } from '@heroicons/react/24/solid';

export const LiveBlogDetailsPublic: FC<LiveBlogDetailsProps> = ({
  liveBlog,
  pageNumber,
  livePosts,
  totalPages,
  publicities,
  setPageNumber,
  isFetched,
}) => {
  const [
    isShareModalOpen,
    { open: onOpenShareModal, close: onCloseShareModal },
  ] = useDisclosure(false);
  const { t } = useTranslation(['liveBlogDetails', 'dashboard']);

  const [shareLink, setShareLink] = useState<string>();

  const handleSharePost = (blogSlug: string) => {
    setShareLink(
      `${process.env.NEXT_PUBLIC_APP_URL}/em-direto/${liveBlog?.slug}#${blogSlug}`
    );
    onOpenShareModal();
  };

  const handleShareLiveBlog = () => {
    setShareLink(
      `${process.env.NEXT_PUBLIC_APP_URL}/em-direto/${liveBlog?.slug}`
    );
    onOpenShareModal();
  };

  return (
    <Grid columns={4}>
      <Grid.Col mb={16} md={4} offsetLg={1} lg={3} order={1} orderLg={2}>
        <TitleDescriptionLiveBlog
          title={liveBlog?.title}
          description={liveBlog?.description}
          titleDirect={t('live')}
        />
      </Grid.Col>
      <Grid.Col md={4} lg={3} order={3} orderLg={4}>
        <Flex w="100%" h={450} pos="relative">
          <Image
            sizes="(max-width: 768px) 900px, 1366px"
            width={1920}
            height={1080}
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
            priority
            alt={liveBlog?.title ?? ''}
            src={liveBlog?.header_image?.key ?? NEWS_HEADER_IMAGE}
          />
        </Flex>
      </Grid.Col>
      <Grid.Col md={4} lg={1} order={2} orderLg={3}>
        {liveBlog?.author && (
          <Stack spacing="xs" w="100%" h="fit-content">
            <Divider />
            <AuthorsCard size={40} author={liveBlog.author} padding={0} />
            <Divider />
            <Flex justify="space-between" align="center">
              <Text variant="caption2" component="span">
                {getFormattedDate(liveBlog.publish_at)}
              </Text>
              <ActionIcon
                id="share-live-blog-button"
                aria-label="share-live-blog-button"
                onClick={handleShareLiveBlog}
              >
                <ShareIcon width={16} />
              </ActionIcon>
            </Flex>
            <Divider />
          </Stack>
        )}
      </Grid.Col>
      <Grid.Col
        orderLg={4}
        lg={1}
        span={0}
        display={{ base: 'none', lg: 'block' }}
      >
        <Flex direction="column" align="flex-start" h="100%">
          <Box pt={70} sx={{ position: 'sticky', top: 0, width: '100%' }}>
            <RandomPublicityComponent
              index={0}
              type="SIDEBAR"
              publicities={publicities ?? []}
            />
          </Box>
        </Flex>
      </Grid.Col>
      <Grid.Col orderLg={5} order={4} lg={2} md={6}>
        <Flex
          pt={70}
          px={12}
          direction="column"
          w={{ lg: '100%', base: '100%' }}
        >
          <Title order={2} variant="h2Orange">
            {t('liveUpdates')}
          </Title>
          {!isFetched && livePosts?.length === 0 ? (
            <LoadingStateComponent />
          ) : isFetched && livePosts?.length !== 0 ? (
            livePosts?.map((livePost) => (
              <LivePost
                key={livePost.slug}
                livePost={livePost}
                handleSharePost={handleSharePost}
              />
            ))
          ) : (
            <EmptyStateComponent />
          )}
          <Flex mt={16}>
            <TablePagination
              pageNumber={pageNumber}
              totalNumberOfPages={totalPages ?? 1}
              setPageNumber={setPageNumber}
            />
          </Flex>
        </Flex>
      </Grid.Col>
      <Grid.Col
        display={{ base: 'none', lg: 'block' }}
        orderLg={6}
        md={0}
        lg={1}
      >
        <Flex direction="column" align="flex-start" h="100%">
          <Box pt={70} sx={{ position: 'sticky', top: 0, width: '100%' }}>
            <RandomPublicityComponent
              index={1}
              type="SIDEBAR"
              publicities={publicities ?? []}
            />
          </Box>
        </Flex>
      </Grid.Col>
      <ShareMenu
        isModalOpen={isShareModalOpen}
        onCloseModal={onCloseShareModal}
        link={shareLink}
        title={liveBlog?.title}
      />
    </Grid>
  );
};
